import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Card, CardContent, Container, CircularProgress, LinearProgress } from '@mui/material';
import { firestore } from '../Firebase';
import Bg from '../Images/about.jpg';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { motion } from 'framer-motion';
import { FaUsers, FaChartBar, FaGraduationCap, FaUserGraduate, FaLaptopCode, FaDatabase } from 'react-icons/fa';
import { TrendingUp, EmojiEvents, School, WorkspacePremium } from '@mui/icons-material';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const AdminHome = () => {
  const [dipComputerScienceSubmissions, setDipComputerScienceSubmissions] = useState(0);
  const [bscComputerScienceSubmissions, setBscComputerScienceSubmissions] = useState(0);
  const [dipInformationSystemsSubmissions, setDipInformationSystemsSubmissions] = useState(0);
  const [bscInformationSystemsSubmissions, setBscInformationSystemsSubmissions] = useState(0);
  const [other, setOther] = useState(0);
  const [loading, setLoading] = useState(true);

  // Vibrant color palette
  const colors = {
    cyan: { primary: '#00d9ff', gradient: 'linear-gradient(135deg, #00d9ff, #00b4d8)' },
    blue: { primary: '#3b82f6', gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)' },
    teal: { primary: '#14b8a6', gradient: 'linear-gradient(135deg, #14b8a6, #0d9488)' },
    green: { primary: '#22c55e', gradient: 'linear-gradient(135deg, #22c55e, #16a34a)' },
    orange: { primary: '#f97316', gradient: 'linear-gradient(135deg, #f97316, #ea580c)' },
    yellow: { primary: '#eab308', gradient: 'linear-gradient(135deg, #eab308, #ca8a04)' },
  };

  const darkGlassStyle = {
    background: "rgba(0, 0, 36, 0.85)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(0, 217, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
  };

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const dipCSSnapshot = await firestore
          .collection('registrations')
          .where('program', '==', 'Dip in Computer Science')
          .get();
        setDipComputerScienceSubmissions(dipCSSnapshot.size);

        const bscCSnapshot = await firestore
          .collection('registrations')
          .where('program', '==', 'Bsc Computer Science')
          .get();
        setBscComputerScienceSubmissions(bscCSnapshot.size);

        const dipISSnapshot = await firestore
          .collection('registrations')
          .where('program', '==', 'Dip Information Systems')
          .get();
        setDipInformationSystemsSubmissions(dipISSnapshot.size);

        const bscISSnapshot = await firestore
          .collection('registrations')
          .where('program', '==', 'Bsc Information Systems')
          .get();
        setBscInformationSystemsSubmissions(bscISSnapshot.size);

        const others = await firestore
          .collection('registrations')
          .where('program', '==', 'other')
          .get();
        setOther(others.size);
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  const totalMembers = dipComputerScienceSubmissions + bscComputerScienceSubmissions + dipInformationSystemsSubmissions + bscInformationSystemsSubmissions + other;

  const barChartData = {
    labels: ['Dip CS', 'BSc CS', 'Dip IS', 'BSc IS', 'Other'],
    datasets: [
      {
        label: 'Registered Members',
        data: [dipComputerScienceSubmissions, bscComputerScienceSubmissions, dipInformationSystemsSubmissions, bscInformationSystemsSubmissions, other],
        backgroundColor: [
          'rgba(0, 217, 255, 0.85)',
          'rgba(59, 130, 246, 0.85)',
          'rgba(20, 184, 166, 0.85)',
          'rgba(34, 197, 94, 0.85)',
          'rgba(249, 115, 22, 0.85)',
        ],
        borderColor: [
          '#00d9ff',
          '#3b82f6',
          '#14b8a6',
          '#22c55e',
          '#f97316',
        ],
        borderWidth: 2,
        borderRadius: 10,
        hoverBackgroundColor: [
          'rgba(0, 217, 255, 1)',
          'rgba(59, 130, 246, 1)',
          'rgba(20, 184, 166, 1)',
          'rgba(34, 197, 94, 1)',
          'rgba(249, 115, 22, 1)',
        ],
      },
    ],
  };

  const doughnutData = {
    labels: ['Dip CS', 'BSc CS', 'Dip IS', 'BSc IS', 'Other'],
    datasets: [
      {
        data: [dipComputerScienceSubmissions, bscComputerScienceSubmissions, dipInformationSystemsSubmissions, bscInformationSystemsSubmissions, other],
        backgroundColor: [
          'rgba(0, 217, 255, 0.9)',
          'rgba(59, 130, 246, 0.9)',
          'rgba(20, 184, 166, 0.9)',
          'rgba(34, 197, 94, 0.9)',
          'rgba(249, 115, 22, 0.9)',
        ],
        borderColor: '#000024',
        borderWidth: 4,
        hoverOffset: 15,
        hoverBorderColor: '#ffffff',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        labels: {
          color: 'white',
          font: { size: 12 }
        }
      },
      title: {
        display: false,
        color: 'white',
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 36, 0.9)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(0, 217, 255, 0.3)',
        borderWidth: 1,
      }
    },
    scales: {
      x: {
        ticks: { color: 'white' },
        grid: { color: 'rgba(0, 217, 255, 0.1)' }
      },
      y: {
        ticks: { color: 'white' },
        grid: { color: 'rgba(0, 217, 255, 0.1)' }
      }
    }
  };

  const statCards = [
    {
      title: 'Total Members',
      value: totalMembers,
      icon: <FaUsers size={28} />,
      color: colors.cyan.primary,
      gradient: colors.cyan.gradient,
      bgGlow: 'rgba(0, 217, 255, 0.15)',
    },
    {
      title: 'Dip Computer Science',
      value: dipComputerScienceSubmissions,
      icon: <FaLaptopCode size={28} />,
      color: colors.blue.primary,
      gradient: colors.blue.gradient,
      bgGlow: 'rgba(59, 130, 246, 0.15)',
    },
    {
      title: 'BSc Computer Science',
      value: bscComputerScienceSubmissions,
      icon: <FaUserGraduate size={28} />,
      color: colors.teal.primary,
      gradient: colors.teal.gradient,
      bgGlow: 'rgba(20, 184, 166, 0.15)',
    },
    {
      title: 'Dip Information Systems',
      value: dipInformationSystemsSubmissions,
      icon: <FaDatabase size={28} />,
      color: colors.green.primary,
      gradient: colors.green.gradient,
      bgGlow: 'rgba(34, 197, 94, 0.15)',
    },
    {
      title: 'BSc Information Systems',
      value: bscInformationSystemsSubmissions,
      icon: <FaGraduationCap size={28} />,
      color: colors.orange.primary,
      gradient: colors.orange.gradient,
      bgGlow: 'rgba(249, 115, 22, 0.15)',
    },
    {
      title: 'Other Programs',
      value: other,
      icon: <FaChartBar size={28} />,
      color: colors.yellow.primary,
      gradient: colors.yellow.gradient,
      bgGlow: 'rgba(234, 179, 8, 0.15)',
    },
  ];

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: `linear-gradient(135deg, rgba(0, 0, 36, 0.95) 0%, rgba(10, 25, 47, 0.9) 50%, rgba(0, 0, 36, 0.95) 100%), url(${Bg})`,
          backgroundSize: 'cover',
        }}
      >
        <CircularProgress sx={{ color: '#00d9ff' }} size={60} />
        <Typography variant="h6" sx={{ color: 'white', mt: 2 }}>
          Loading Dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, rgba(0, 0, 36, 0.95) 0%, rgba(10, 25, 47, 0.9) 50%, rgba(0, 0, 36, 0.95) 100%), url(${Bg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        color: 'white',
        pt: { xs: 12, md: 14 },
        pb: 8,
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: '18px',
                background: 'linear-gradient(135deg, #00d9ff, #3b82f6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 10px 30px rgba(0, 217, 255, 0.3)',
              }}
            >
              <TrendingUp sx={{ fontSize: 32, color: 'white' }} />
            </Box>
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  background: 'linear-gradient(90deg, #ffffff, #00d9ff, #3b82f6)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Admin Dashboard
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Welcome back! Here's an overview of your chapter's performance.
              </Typography>
            </Box>
          </Box>
        </motion.div>

        {/* Colorful Divider */}
        <Box
          sx={{
            height: '3px',
            width: '200px',
            background: 'linear-gradient(90deg, #00d9ff, #3b82f6, #14b8a6)',
            borderRadius: '2px',
            mb: 4,
          }}
        />

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {statCards.map((stat, index) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -8, scale: 1.03 }}
              >
                <Card
                  sx={{
                    borderRadius: '20px',
                    background: `linear-gradient(145deg, ${stat.bgGlow}, rgba(0, 0, 36, 0.9))`,
                    backdropFilter: "blur(20px)",
                    border: `1px solid ${stat.color}30`,
                    boxShadow: `0 8px 32px ${stat.bgGlow}`,
                    transition: 'all 0.4s ease',
                    overflow: 'hidden',
                    position: 'relative',
                    '&:hover': {
                      border: `1px solid ${stat.color}`,
                      boxShadow: `0 20px 50px ${stat.color}40`,
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: stat.gradient,
                    },
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <Box
                      sx={{
                        width: 65,
                        height: 65,
                        borderRadius: '18px',
                        background: stat.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                        color: 'white',
                        boxShadow: `0 8px 25px ${stat.color}50`,
                      }}
                    >
                      {stat.icon}
                    </Box>
                    <Typography
                      variant="h3"
                      fontWeight={800}
                      sx={{
                        background: stat.gradient,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1, fontWeight: 500 }}>
                      {stat.title}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Charts Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <TrendingUp sx={{ color: '#00d9ff', fontSize: 32 }} />
            <Typography variant="h5" fontWeight={700} sx={{ color: 'white' }}>
              System{' '}
              <Box
                component="span"
                sx={{
                  background: 'linear-gradient(90deg, #00d9ff, #3b82f6, #14b8a6)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Analytics
              </Box>
            </Typography>
          </Box>
        </motion.div>

        <Grid container spacing={4}>
          {/* Bar Chart */}
          <Grid item xs={12} md={8}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Card
                sx={{
                  borderRadius: '25px',
                  ...darkGlassStyle,
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #00d9ff, #3b82f6, #14b8a6, #22c55e, #f97316)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Box
                      sx={{
                        width: 55,
                        height: 55,
                        borderRadius: '15px',
                        background: 'linear-gradient(135deg, #3b82f6, #14b8a6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 8px 25px rgba(59, 130, 246, 0.4)',
                      }}
                    >
                      <FaChartBar size={26} color="white" />
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>
                        Members by Program
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                        Real-time registration data
                      </Typography>
                    </Box>
                  </Box>
                  <Bar data={barChartData} options={chartOptions} />
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Doughnut Chart */}
          <Grid item xs={12} md={4}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <Card
                sx={{
                  borderRadius: '25px',
                  ...darkGlassStyle,
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #22c55e, #00d9ff)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Box
                      sx={{
                        width: 55,
                        height: 55,
                        borderRadius: '15px',
                        background: 'linear-gradient(135deg, #22c55e, #00d9ff)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 8px 25px rgba(34, 197, 94, 0.4)',
                      }}
                    >
                      <FaUsers size={26} color="white" />
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>
                        Distribution
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                        Membership breakdown
                      </Typography>
                    </Box>
                  </Box>
                  <Doughnut
                    data={doughnutData}
                    options={{
                      responsive: true,
                      cutout: '65%',
                      plugins: {
                        legend: {
                          position: 'bottom',
                          labels: {
                            color: 'white',
                            padding: 15,
                            usePointStyle: true,
                            pointStyle: 'circle',
                          }
                        },
                        tooltip: {
                          backgroundColor: 'rgba(0, 0, 36, 0.9)',
                          titleColor: 'white',
                          bodyColor: 'white',
                          borderColor: 'rgba(0, 217, 255, 0.3)',
                          borderWidth: 1,
                        }
                      }
                    }}
                  />
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>

        {/* Quick Stats Row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <Grid container spacing={3} sx={{ mt: 4 }}>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  borderRadius: '20px',
                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(0, 0, 36, 0.9))',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  p: 3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '12px',
                      background: colors.blue.gradient,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <EmojiEvents sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Growth Rate
                    </Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ color: '#3b82f6' }}>
                      +{totalMembers > 0 ? Math.round((totalMembers / 100) * 15) : 0}%
                    </Typography>
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={75}
                  sx={{
                    mt: 2,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: 'rgba(59, 130, 246, 0.2)',
                    '& .MuiLinearProgress-bar': {
                      background: colors.blue.gradient,
                      borderRadius: 3,
                    },
                  }}
                />
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  borderRadius: '20px',
                  background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(0, 0, 36, 0.9))',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  p: 3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '12px',
                      background: colors.green.gradient,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <School sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Active Programs
                    </Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ color: '#22c55e' }}>
                      5 Programs
                    </Typography>
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={100}
                  sx={{
                    mt: 2,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: 'rgba(34, 197, 94, 0.2)',
                    '& .MuiLinearProgress-bar': {
                      background: colors.green.gradient,
                      borderRadius: 3,
                    },
                  }}
                />
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  borderRadius: '20px',
                  background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.2), rgba(0, 0, 36, 0.9))',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(249, 115, 22, 0.3)',
                  p: 3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '12px',
                      background: colors.orange.gradient,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <WorkspacePremium sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Top Program
                    </Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ color: '#f97316' }}>
                      {bscComputerScienceSubmissions >= dipComputerScienceSubmissions ? 'BSc CS' : 'Dip CS'}
                    </Typography>
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={85}
                  sx={{
                    mt: 2,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: 'rgba(249, 115, 22, 0.2)',
                    '& .MuiLinearProgress-bar': {
                      background: colors.orange.gradient,
                      borderRadius: 3,
                    },
                  }}
                />
              </Card>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default AdminHome;
